import React, { useState } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Input from "@thingsw/pitta-design-system/dist/components/Input";
import {
  Field,
  InjectedFormProps,
  WrappedFieldProps,
  reduxForm,
  submit,
} from "redux-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { TFunction } from "i18next";
import * as yup from "yup";
import validator from "./validator";
import { Typography } from "@thingsw/pitta-design-system";
import _ from "lodash";
import { FormHelperText } from "@material-ui/core";
import {
  Webviewer,
  IChangeNameForm,
  LightColors,
} from "@thingsw/pitta-modules";

const schema = yup.object().shape({
  firstName: yup
    .string()
    .trim()
    .min(1, "The name must_32")
    .max(32, "The name must_32")
    .test("unallowed-check", "Unallowed character detected", (val) => {
      return (
        (val?.indexOf('"') ?? -1) === -1 && (val?.indexOf("\\") ?? -1) === -1
      );
    }),
});

interface FieldProps {
  label?: string;
  value?: string;
  helperText?: string;
  t: TFunction;
}

const renderEmailField = ({
  label,
  input,
  helperText,
  meta: { touched, invalid, error },
  t,
  ...custom
}: WrappedFieldProps & FieldProps) => {
  return (
    <Input label={label} error={touched && error} {...input} {...custom} />
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  formDiv: {
    width: 352,
    marginTop: theme.spacing(2),
    [theme.breakpoints.down(Webviewer.mobile)]: {
      width: 288,
    },
  },
  marginB21: {
    marginBottom: theme.spacing(3),
    "&:last-child": {
      marginBottom: theme.spacing(2),
    },
  },
  cntDiv: {
    display: "flex",
    justifyContent: "flex-end",
    margin: "3px 16px 24px 16px",
    [theme.breakpoints.down(Webviewer.mobile)]: {
      marginTop: "4px",
    },
  },
  errStyle: {
    display: "flex",
    alignItems: "center",
  },
}));

const ChangeName = (props: InjectedFormProps<IChangeNameForm, {}>) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { handleSubmit } = props;
  const { t } = useTranslation();
  const [firstError, setFirstError] = useState<string>();
  const [firstName, setFirstName] = useState("");

  const handleEnterkey = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      dispatch(submit("ChangeNameForm"));
    }
  };

  const handleFirstNameChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    console.log("value", value);

    setFirstName(value);
    let error: { [key: string]: string } = {};
    try {
      await schema.validate({ firstName: value }, { abortEarly: false });
    } catch (err) {
      error = _.reduce(
        err.inner,
        (r, i) => ({ ...r, [i.path]: i.message }),
        {} as { [key: string]: string }
      );
    } finally {
      setFirstError(error["firstName"]);
    }
  };

  return (
    <form
      className={classes.formDiv}
      onSubmit={handleSubmit}
      onKeyDown={handleEnterkey}
    >
      <div>
        <Field
          name="firstName"
          label={t("Name")}
          onChange={handleFirstNameChange}
          component={renderEmailField}
          value={firstName}
          error={!!firstError}
          autoFocus
          t={t}
        />
      </div>
      <div
        className={classes.cntDiv}
        style={{ justifyContent: firstError && "space-between" }}
      >
        {firstError && (
          <FormHelperText
            style={{
              color: LightColors.secondary["11"],
              marginTop: 0,
            }}
          >
            {t(firstError)}
          </FormHelperText>
        )}
        <Typography
          category="Default"
          variant="Caption"
          htmlColor={LightColors.primary["2"]}
          className={classes.errStyle}
        >
          {firstName.length}/32
        </Typography>
      </div>
    </form>
  );
};

const ChangeNameForm = reduxForm<IChangeNameForm, {}>({
  form: "ChangeNameForm",
  asyncValidate: validator(schema),
})(ChangeName);

export default ChangeNameForm;
